// router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store'; // Vuex ストアをインポート
import { currentRoleFromStoreOrLocalStorage, isAdminRole, canUseIes4Import, canUseIes4BulkImport } from '@/utils/roleAuthorization';

// ログイン
import Login from '../views/Login.vue';
import SsoEntryPage from '../views/SsoEntryPage.vue';

// ホーム
import Home from '../views/Homes/Home.vue';

// ユーザ情報
import UserList from '../views/Users/UserList.vue';
import UserCreate from '../views/Users/UserCreate.vue';
import UserEdit from '../views/Users/UserEdit.vue';
import UserDetail from '../views/Users/UserDetail.vue';

// 顧客情報
import CustomerList from '../views/Customers/CustomerList.vue';
import CustomerCreate from '../views/Customers/CustomerCreate.vue';
import CustomerEdit from '../views/Customers/CustomerEdit.vue';
import CustomerDetail from '../views/Customers/CustomerDetail.vue';

// 建物情報
import BuildingList from '../views/Buildings/BuildingList.vue';
import BuildingCreate from '../views/Buildings/BuildingCreate.vue';
import BuildingEdit from '../views/Buildings/BuildingEdit.vue';
import BuildingDetail from '../views/Buildings/BuildingDetail.vue';

// 建物種別情報
import BuildingTypeList from '../views/BuildingTypes/BuildingTypeList.vue';
import BuildingTypeCreate from '../views/BuildingTypes/BuildingTypeCreate.vue';
import BuildingTypeEdit from '../views/BuildingTypes/BuildingTypeEdit.vue';
import BuildingTypeDetail from '../views/BuildingTypes/BuildingTypeDetail.vue';

// 測定機器
import DeviceList from '../views/Devices/DeviceList.vue';
import DeviceCreate from '../views/Devices/DeviceCreate.vue';
import DeviceEdit from '../views/Devices/DeviceEdit.vue';
import DeviceDetail from '../views/Devices/DeviceDetail.vue';

// 測定種別
import MeasureTypeList from '../views/MeasureTypes/MeasureTypeList.vue';
import MeasureTypeCreate from '../views/MeasureTypes/MeasureTypeCreate.vue';
import MeasureTypeEdit from '../views/MeasureTypes/MeasureTypeEdit.vue';
import MeasureTypeDetail from '../views/MeasureTypes/MeasureTypeDetail.vue';

// 相対湿度マトリクス
import RelativeHumidityMatrix from '../views/RelativeHumidityMatrixs/RelativeHumidityMatrix.vue';

// 空気環境測定基準値
import MeasureStdList from '../views/MeasureStds/MeasureStdList.vue';
import MeasureStdCreate from '../views/MeasureStds/MeasureStdCreate.vue';
import MeasureStdEdit from '../views/MeasureStds/MeasureStdEdit.vue';
import MeasureStdDetail from '../views/MeasureStds/MeasureStdDetail.vue';

// 測定報告書
import MeasurementReportList from '../views/MeasurementReports/MeasurementReportList.vue';
import CustomerBuildingList from '../views/MeasurementReports/CustomerBuildingList.vue';
import CustomerBuildingReportList from '../views/MeasurementReports/CustomerBuildingReportList.vue';
import CustomerMeasurementReportCreate from '../views/MeasurementReports/MeasurementReportCreate.vue';
import CustomerMeasurementReportCopy from '../views/MeasurementReports/MeasurementReportCopy.vue';
import CustomerMeasurementReportEdit from '../views/MeasurementReports/MeasurementReportEdit.vue';
import MeasurementReportDetail from '../views/MeasurementReports/MeasurementReportDetail.vue';
import MeasurementReportIes4Import from '../views/MeasurementReports/MeasurementReportIes4Import.vue';
import MeasurementReportIes4BulkImport from '../views/MeasurementReports/MeasurementReportIes4BulkImport.vue';

const routes = [
  { path: '/login', name: 'Login', component: Login },
  { path: '/sso-entry', name: 'SsoEntry', component: SsoEntryPage },

  // ホーム
  { path: '/home', name: 'Home', component: Home, meta: {requiresAuth: true} },

  // ユーザ情報ルート
  { path: '/users', name: 'UserList', component: UserList, meta: {requiresAuth: true} },
  { path: '/create-user', name: 'UserCreate', component: UserCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/user-edit/:id', name: 'UserEdit', component: UserEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/users/:id', name: 'UserDetail', component: UserDetail, props: true, meta: {requiresAuth: true} },

  // 顧客情報ルート
  { path: '/customers', name: 'CustomerList', component: CustomerList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-customer', name: 'CustomerCreate', component: CustomerCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/customer-edit/:id', name: 'CustomerEdit', component: CustomerEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/customers/:id', name: 'CustomerDetail', component: CustomerDetail, props: true, meta: {requiresAuth: true} },

  // 建物情報ルート
  { path: '/buildings', name: 'BuildingList', component: BuildingList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-building', name: 'BuildingCreate', component: BuildingCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/building-edit/:id', name: 'BuildingEdit', component: BuildingEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/buildings/:id', name: 'BuildingDetail', component: BuildingDetail, props: true, meta: {requiresAuth: true} },

  // 建物種別情報ルート
  { path: '/building-types', name: 'BuildingTypeList', component: BuildingTypeList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-building-type', name: 'BuildingTypeCreate', component: BuildingTypeCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/building-type-edit/:id', name: 'BuildingTypeEdit', component: BuildingTypeEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/building-types/:id', name: 'BuildingTypeDetail', component: BuildingTypeDetail, props: true, meta: {requiresAuth: true} },

  // 測定機器情報ルート
  { path: '/devices', name: 'DeviceList', component: DeviceList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-device', name: 'DeviceCreate', component: DeviceCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/device-edit/:id', name: 'DeviceEdit', component: DeviceEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/devices/:id', name: 'DeviceDetail', component: DeviceDetail, props: true, meta: {requiresAuth: true} },

  // 測定種別情報ルート
  { path: '/measure-types', name: 'MeasureTypeList', component: MeasureTypeList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-measure-type', name: 'MeasureTypeCreate', component: MeasureTypeCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/measure-type-edit/:id', name: 'MeasureTypeEdit', component: MeasureTypeEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/measure-types/:id', name: 'MeasureTypeDetail', component: MeasureTypeDetail, props: true, meta: {requiresAuth: true} },

  // 相対湿度マトリクスルート
  { path: '/relative_humidity_matrixs', name: 'RelativeHumidityMatrix', component: RelativeHumidityMatrix, requiresAuth: true, meta: {requiresAuth: true} },

  // 空気環境測定基準値ルート
  { path: '/measure-stds', name: 'MeasureStdList', component: MeasureStdList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-measure-std', name: 'MeasureStdCreate', component: MeasureStdCreate, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/measure-std-edit/:id', name: 'MeasureStdEdit', component: MeasureStdEdit, props: true, meta: {requiresAuth: true, requiresAdmin: true} },
  { path: '/measure-stds/:id', name: 'MeasureStdDetail', component: MeasureStdDetail, props: true, meta: {requiresAuth: true} },

  // 測定報告書ルート
  { path: '/measurement_reports', name: 'MeasurementReportList', component: MeasurementReportList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/ies4-customer-measurement-report/:customer_id/:building_id', name: 'MeasurementReportIes4Import', component: MeasurementReportIes4Import, meta: {requiresAuth: true, requiresIes4Import: true} },
  { path: '/ies4-bulk-import', name: 'MeasurementReportIes4BulkImport', component: MeasurementReportIes4BulkImport, meta: {requiresAuth: true, requiresIes4BulkImport: true} },
  { path: '/measurement-customers', name: 'CustomerBuildingList', component: CustomerBuildingList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/building-measurement-reports/:customer_id/:building_id', name: 'CustomerBuildingReportList', component: CustomerBuildingReportList, requiresAuth: true, meta: {requiresAuth: true} },
  { path: '/create-customer-measurement-report/:customer_id/:building_id', name: 'CustomerMeasurementReportCreate', component: CustomerMeasurementReportCreate, meta: {requiresAuth: true} },
  { path: '/copy-customer-measurement-report/:id/:customer_id/:building_id', name: 'CustomerMeasurementReportCopy', component: CustomerMeasurementReportCopy, props: true, meta: {requiresAuth: true} },
  { path: '/customer-measurement-report-edit/:id/:customer_id/:building_id', name: 'CustomerMeasurementReportEdit', component: CustomerMeasurementReportEdit, props: true, meta: {requiresAuth: true} },
  { path: '/measurement_reports/:id', name: 'MeasurementReportDetail', component: MeasurementReportDetail, props: true, meta: {requiresAuth: true} },

];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach((to, from, next) => {
  const isDemoMode = process.env.NODE_ENV !== 'production' && to.query.demo === '1';
  if (isDemoMode) {
    store.commit('setLoggedIn', true);
    store.commit('setUserName', 'システム管理者');
    store.commit('setUserRole', '999');
    localStorage.setItem('userEmail', 'admin@example.com');
  }

  const isLoggedIn = store.state.isLoggedIn;
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresIes4Import = to.matched.some(record => record.meta.requiresIes4Import);
  const requiresIes4BulkImport = to.matched.some(record => record.meta.requiresIes4BulkImport);

  if (requiresAuth && !isLoggedIn && !isDemoMode) {
    next('/login'); // ログイン画面にリダイレクト
    return;
  }

  if (requiresAdmin) {
    const currentRole = currentRoleFromStoreOrLocalStorage(store.state.userRole);

    if (!isAdminRole(currentRole)) {
      store.commit('setFlashMessage', { message: 'この操作を実行する権限がありません', type: 'warning' });
      next('/home');
      return;
    }
  }

  if (requiresIes4Import) {
    const currentRole = currentRoleFromStoreOrLocalStorage(store.state.userRole);

    if (!isDemoMode && !canUseIes4Import(currentRole)) {
      store.commit('setFlashMessage', { message: 'IES4取込機能へのアクセス権限がありません', type: 'warning' });
      next('/home');
      return;
    }
  }

  // 一括取込はシステム管理者のみ。デモモードでも開けない。
  if (requiresIes4BulkImport) {
    const currentRole = currentRoleFromStoreOrLocalStorage(store.state.userRole);

    if (!canUseIes4BulkImport(currentRole)) {
      store.commit('setFlashMessage', { message: 'IES4一括取込機能へのアクセス権限がありません', type: 'warning' });
      next('/home');
      return;
    }
  }

  next(); // そのままルートに進む
});

export default router;
